import { vocative as vocativeUtil } from 'czech-vocative';
import { formatDistance, formatRelative, type FormatDistanceOptions, type FormatRelativeOptions } from 'date-fns';

import { type CURRENCY } from '../constants/currency';
import { LANGUAGE, languageEnumBase } from '../constants/language';
import {
	formatDateRange as formatDateRangeUtil,
	formatDate as formatDateUtil,
	formatNumber as formatNumberUtil,
	formatPrice as formatPriceUtil,
	getDateFnsLocale,
} from '../utils/format';
import { isDefined } from '../utils/guards';
import { upperFirst } from '../utils/string';

/**
 * Represents a virtual currency with localization options
 */
export interface VirtualCurrency {
	/** Exchange rate relative to real currency */
	rate: number;
	/** Base name of the virtual currency */
	name: string;
	/** Optional internationalization data for the currency */
	intl?: VirtualCurrencyIntl;
	/** Optional list of internationalization data for multiple languages */
	intlList?: VirtualCurrencyIntl[];
}

/**
 * Internationalization data for virtual currency pluralization forms
 */
export interface VirtualCurrencyIntl {
	/** Language identifier for this localization set */
	languageId: LANGUAGE;
	/** Form used for few items (typically 2-4 in Slavic languages) */
	few: string;
	/** Form used for many items */
	many: string;
	/** Form used for exactly one item */
	one: string;
	/** Default form used when other forms don't apply */
	other: string;
}

/**
 * Configuration options for the formatter
 */
export interface CreateFormatterOptions {
	/** Language identifier to use for formatting */
	languageId: LANGUAGE;
	/** Optional timezone identifier (defaults to 'Europe/Prague') */
	timezone?: string;
}

/**
 * Creates formatting utilities based on the provided language and locale options
 * @param inputOptions Configuration options for the formatter
 * @returns Object containing various formatting utilities
 */
export const createFormatter = (inputOptions: CreateFormatterOptions) => {
	const { languageId, timezone = 'Europe/Prague' } = inputOptions;
	const langEnum = languageEnumBase.get(languageId);
	const dateFnsLocale = getDateFnsLocale(langEnum.code);

	/**
	 * Localized number formatter
	 * @param value Number to format
	 * @param settings Optional Intl.NumberFormatOptions
	 * @returns Formatted number string
	 */
	const formatNumber = (value: number, settings?: Intl.NumberFormatOptions) => formatNumberUtil(value, langEnum.code, settings);

	/**
	 * Localized byte size formatter
	 * @param value Size in bytes
	 * @param settings Optional Intl.NumberFormatOptions
	 * @returns Formatted byte size with appropriate unit (KB, MB, GB)
	 */
	const formatByteSize = (value: number, settings?: Intl.NumberFormatOptions) => {
		const options = { style: 'unit', ...settings } as const;

		/** format as GB */
		if (value >= 1_000_000_000)
			return formatNumber(value / 1_000_000_000, {
				unit: 'gigabyte',
				...options,
			});

		/** format as MB */
		if (value >= 1_000_000) {
			return formatNumber(value / 1_000_000, {
				unit: 'megabyte',
				...options,
			});
		}

		/** format as KB */
		return formatNumber(value / 1_000, {
			unit: 'kilobyte',
			...options,
		});
	};

	/**
	 * Localized percent formatter
	 * @param value Number to format as percentage (10000 = 100%)
	 * @param settings Optional Intl.NumberFormatOptions
	 * @returns Formatted percentage string
	 */
	const formatPercent = (value: number, settings?: Intl.NumberFormatOptions): string => {
		/** divide by 100 to get decimal places (2150 -> 21,50) and then divide once more to get actual percentage value (21,50 -> 0,215) */
		if (settings?.maximumFractionDigits === 0 && value < 1_00 && value !== 0) return `< ${formatPercent(1_00)}`;
		return formatNumberUtil(value / 100 / 100, langEnum.code, {
			maximumFractionDigits: 2,
			...settings,
			style: 'percent',
		});
	};

	/**
	 * Localized price formatter
	 * @param value Price value in minor units (cents)
	 * @param currencyId Currency identifier
	 * @param settings Optional Intl.NumberFormatOptions
	 * @returns Formatted price string with currency symbol
	 */
	const formatPrice = (value: number, currencyId: number, settings?: Intl.NumberFormatOptions) =>
		formatPriceUtil(currencyId, langEnum.code, settings).format(Number(value) / 100);

	/**
	 * Localized virtual price formatter
	 * @param value Price value in minor units (cents)
	 * @param virtualCurrency Virtual currency configuration
	 * @returns Formatted virtual price with appropriate unit
	 */
	const formatVirtualPrice = (value: number, virtualCurrency: VirtualCurrency) => {
		const intlTexts = isDefined(virtualCurrency.intl)
			? virtualCurrency.intl
			: isDefined(virtualCurrency.intlList)
			? virtualCurrency.intlList.find((localization) => languageId === localization.languageId)
			: null;
		const convertedPrice = value / 100 / virtualCurrency.rate;

		/** not virtual currency texts defined */
		if (!isDefined(intlTexts)) return `${formatNumber(convertedPrice)} ${virtualCurrency.name}`;

		/** virtual currency texts defined */
		const text =
			convertedPrice === 1
				? intlTexts.one
				: convertedPrice > 1 && convertedPrice < 5
				? intlTexts.few
				: convertedPrice >= 5
				? intlTexts.many
				: intlTexts.other;
		return `${formatNumber(convertedPrice)} ${text}`;
	};

	/**
	 * Formats virtual currency rate against real currency
	 * @param currencyId Real currency identifier
	 * @param virtualCurrency Virtual currency configuration
	 * @returns Formatted exchange rate string
	 */
	const formatVirtualCurrencyRate = (currencyId: CURRENCY, virtualCurrency: VirtualCurrency) => {
		const isVirtualCurrencyHigher = virtualCurrency.rate > 1;
		if (isVirtualCurrencyHigher) {
			/** e.g. 1 bolt = 100 Kč */
			const lower = formatVirtualPrice(100 * virtualCurrency.rate, virtualCurrency);
			const higher = formatPrice(virtualCurrency.rate * 100, currencyId);
			return `${lower} = ${higher}`;
		}

		/** e.g. 1 Kč = 100 bolts */
		const lower = formatPrice(100, currencyId);
		const higher = formatVirtualPrice(100, virtualCurrency);
		return `${lower} = ${higher}`;
	};

	/**
	 * Returns localized currency parts
	 * @param currencyId Currency identifier
	 * @param settings Optional Intl.NumberFormatOptions
	 * @returns Array of formatted currency parts
	 */
	const currencyParts = (currencyId: number, settings?: Intl.NumberFormatOptions) =>
		formatPriceUtil(currencyId, langEnum.code, settings).formatToParts(0);

	/**
	 * Return localized currency sign
	 * @param currencyId Currency identifier
	 * @param settings Optional Intl.NumberFormatOptions
	 * @returns Currency symbol
	 */
	const currencySign = (currencyId: number, settings?: Intl.NumberFormatOptions) => {
		const currencyPart = currencyParts(currencyId, settings).find(({ type }) => type === 'currency');
		if (!isDefined(currencyPart)) throw new Error('Currency sign not found');
		return currencyPart.value;
	};

	/**
	 * Localized date formatter
	 * @param date Date to format
	 * @param dateFormat Format string
	 * @param options Optional formatting options
	 * @returns Formatted date string
	 */
	const formatDate: typeof formatDateUtil = (date, dateFormat, options) =>
		formatDateUtil(date, dateFormat, {
			locale: dateFnsLocale,
			...options,
		});

	/**
	 * Localized date range formatter
	 * @param startDate Start date of the range
	 * @param endDate Optional end date of the range
	 * @param options Optional formatting options
	 * @returns Formatted date range string
	 */
	const formatDateRange: typeof formatDateRangeUtil = (startDate, endDate, options) =>
		formatDateRangeUtil(startDate, endDate, {
			locale: dateFnsLocale,
			...options,
		});

	/**
	 * Localized event date range formatter with event-specific handling
	 * @param startDate Start date of the event
	 * @param endDate Optional end date of the event
	 * @param options Optional formatting options
	 * @returns Formatted event date range string
	 */
	const formatEventDateRange: typeof formatDateRange = (startDate, endDate, options) =>
		formatDateRange(startDate, endDate, {
			...options,
			eventLike: true,
		});

	/**
	 * Localized relative date formatter
	 * @param date Date to format
	 * @param now Reference date (defaults to current date)
	 * @param options Optional formatting options
	 * @returns Formatted relative date string
	 */
	const formatDateRelative = (date: Date, now?: Date, options?: FormatRelativeOptions) => {
		const _now = isDefined(now) ? new Date(now) : new Date();
		return formatRelative(date, _now, { locale: dateFnsLocale, ...options });
	};

	/**
	 * Localized time distance formatter
	 * @param date Date to calculate distance from current time
	 * @param options Optional formatting options
	 * @returns Formatted time distance string
	 */
	const formatDateTimeDistance = (date: Date, options?: FormatDistanceOptions) => {
		const now = new Date();
		return formatDistance(date, now, { locale: dateFnsLocale, includeSeconds: true, addSuffix: true, ...options });
	};

	/**
	 * Returns time of day based on given date
	 * @param date Reference date or current date if not provided
	 * @returns Time of day (morning, forenoon, afternoon, noon, evening, night)
	 */
	const getTimeOfDay = (date?: Date) => {
		const now = isDefined(date) ? date : new Date();
		const hours = now.getHours();
		if (hours >= 6 && hours < 10) return 'morning' as const;
		if (hours >= 10 && hours < 12) return 'forenoon' as const;
		if (hours === 12) return 'noon' as const;
		if (hours >= 12 && hours < 17) return 'afternoon' as const;
		if (hours >= 17 && hours < 20) return 'evening' as const;
		return 'night' as const;
	};

	/**
	 * Returns vocative name for supported languages
	 * @param name Name to be transformed
	 * @returns Transformed name or original name if not supported
	 */
	const vocative = (name: string) => {
		const shouldVokativ = [LANGUAGE.CZECH].includes(languageId);
		if (shouldVokativ) return upperFirst(vocativeUtil(name));
		return upperFirst(name);
	};

	return {
		timezone,
		language: langEnum,
		dateFnsLocale,
		vocative,
		number: formatNumber,
		percent: formatPercent,
		price: formatPrice,
		currencyParts,
		currencySign,
		date: formatDate,
		eventDateRange: formatEventDateRange,
		dateRange: formatDateRange,
		dateRelative: formatDateRelative,
		dateDistance: formatDateTimeDistance,
		byteSize: formatByteSize,
		virtualPrice: formatVirtualPrice,
		virtualCurrencyRate: formatVirtualCurrencyRate,
		getTimeOfDay,
	};
};
